import OneSignal from "react-onesignal";

const OLD_VAPID_KEY = "BBAyAFopevM8k-cw41K1yw9B6Mkp39z4mYMBJD0FD3yZwuhyFXX2pnmwnXrdJ6kGRB7UaNF-fr_fpm-HM_qzl_0";

let initPromise = null;
let initError = null;

async function cleanupLegacySubscription() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    const key = sub?.options?.applicationServerKey;
    if (!key) return;
    const b64 = btoa(String.fromCharCode(...new Uint8Array(key)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    if (b64 === OLD_VAPID_KEY) {
      await sub.unsubscribe();
      console.info("Legacy push subscription removed");
    }
  } catch {}
}

export function initOneSignal() {
  const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
  if (!appId || typeof window === "undefined") return Promise.resolve(false);
  if (!initPromise) {
    initPromise = (async () => {
      try {
        await cleanupLegacySubscription();
        await OneSignal.init({
          appId,
          allowLocalhostAsSecureOrigin: true,
          autoResubscribe: true,
          notifyButton: { enable: false },
        });
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            const ps = OneSignal.User.PushSubscription;
            if (!ps.token) {
              try { await ps.optOut(); } catch {}
              await ps.optIn();
            }
          } catch {}
        }
        return true;
      } catch (e) {
        initError = e?.message || String(e);
        console.warn("OneSignal init failed:", initError);
        if (/already initialized/i.test(initError)) return true;
        return false;
      }
    })();
  }
  return initPromise;
}

export async function osLogin(userId) {
  if (!(await initOneSignal())) return;
  try {
    await OneSignal.login(String(userId));
  } catch (e) {
    console.warn("OneSignal login failed:", e?.message || e);
  }
}

export async function osLogout() {
  if (!(await initOneSignal())) return;
  try {
    await OneSignal.logout();
  } catch {}
}

export function pushSupported() {
  return "serviceWorker" in navigator && "Notification" in window;
}

export async function isPushEnabled() {
  if (!(await initOneSignal())) return false;
  try {
    const ps = OneSignal.User.PushSubscription;
    return !!ps.optedIn && !!ps.token;
  } catch {
    return false;
  }
}

export async function enablePush() {
  if (!(await initOneSignal())) throw new Error("not_ready");
  await cleanupLegacySubscription();
  await OneSignal.Notifications.requestPermission();
  if (Notification.permission !== "granted") throw new Error("permission_denied");
  await OneSignal.User.PushSubscription.optIn();
}

export async function disablePush() {
  if (!(await initOneSignal())) return;
  try {
    await OneSignal.User.PushSubscription.optOut();
  } catch {}
}

export async function getPushDiag() {
  const diag = {
    supported: pushSupported(),
    permission: typeof Notification !== "undefined" ? Notification.permission : "non supporté",
    origin: window.location.origin,
  };
  diag.initOk = await initOneSignal();
  if (initError) diag.initError = initError;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    diag.workers = regs.map((r) => `${((r.active || r.installing || r.waiting) || {}).scriptURL || "?"} [scope: ${r.scope}]`);
  } catch (e) {
    diag.workers = [String(e)];
  }
  try {
    const ps = OneSignal.User.PushSubscription;
    diag.subscriptionId = ps.id || null;
    diag.token = ps.token ? ps.token.slice(0, 40) + "…" : null;
    diag.optedIn = !!ps.optedIn;
  } catch (e) {
    diag.sdkError = String(e?.message || e);
  }
  try {
    diag.externalId = OneSignal.User.externalId || null;
  } catch {}
  return diag;
}

export async function forcePushRepair() {
  if (!(await initOneSignal())) throw new Error("SDK non initialisé : " + (initError || "raison inconnue"));
  await cleanupLegacySubscription();
  await OneSignal.Notifications.requestPermission();
  if (Notification.permission !== "granted") throw new Error("Permission navigateur : " + Notification.permission);
  const ps = OneSignal.User.PushSubscription;
  try { await ps.optOut(); } catch {}
  await ps.optIn();
  await new Promise((r) => setTimeout(r, 3000));
  return { id: ps.id || null, token: ps.token ? "OK" : null, optedIn: !!ps.optedIn };
}
